import { MedplumClient, parseReference } from "@medplum/core";
import type { Reference } from "@medplum/fhirtypes";

import type { ReferenceDisplayCache } from "@/billing/types/reference-displays";
import { getDisplayStringAsync } from "@/utils/medplum";
import { getMedplumErrorIssueCodes, getMedplumErrorStatus } from "@/utils/medplum-http-error";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

/** OperationOutcome issue codes for a resource the viewer cannot read or that no longer exists. */
const UNREADABLE_ISSUE_CODES = new Set(["forbidden", "security", "not-found", "deleted"]);

/** HTTP statuses for the same outcomes. A soft-deleted resource answers 410 Gone, not 404. */
const UNREADABLE_STATUSES = new Set([403, 404, 410]);

/**
 * True when a read failed because the resource is permanently unreadable (403/404/410).
 *
 * Only the structured status and OperationOutcome issue codes count. The error message is ignored,
 * because a transient failure whose text happens to say "not found" must still be retried.
 */
function isUnreadableReferenceError(error: unknown): boolean {
  const status = getMedplumErrorStatus(error);
  if (status !== undefined && UNREADABLE_STATUSES.has(status)) return true;
  return getMedplumErrorIssueCodes(error).some((code) => UNREADABLE_ISSUE_CODES.has(code));
}

type ReferenceResolution =
  | { referenceString: string; isResolved: true; display: string }
  | { referenceString: string; isResolved: false };

/** Reads still in flight, per client, so overlapping runs share one read per reference. */
const inFlightReads = new WeakMap<MedplumClient, Map<string, Promise<ReferenceResolution>>>();

/**
 * Drops a failed read from the client's request cache. The client caches the request promise and
 * keeps a rejected one until `cacheTime` passes, so without this a retry would get the same
 * rejection back without making a request. Best effort: a malformed reference has no URL to drop.
 */
function evictFailedRead(medplum: MedplumClient, reference: Reference): void {
  try {
    const [resourceType, id] = parseReference(reference);
    medplum.invalidateUrl(medplum.fhirUrl(resourceType, id));
  } catch {
    // Nothing was cached under a URL that cannot be built.
  }
}

/** Reads one reference's display and classifies the outcome; never throws. */
async function readOnce(
  medplum: MedplumClient,
  referenceString: string,
  reference: Reference,
): Promise<ReferenceResolution> {
  try {
    const display = await getDisplayStringAsync(medplum, reference);
    return { referenceString, isResolved: true, display };
  } catch (error) {
    if (isUnreadableReferenceError(error)) {
      return { referenceString, isResolved: true, display: "" };
    }
    evictFailedRead(medplum, reference);
    // Report the reference only. A payer name is PHI and must not reach Sentry.
    captureExceptionWithoutPhi("Failed to resolve reference display", error, {
      reference: referenceString,
    });
    return { referenceString, isResolved: false };
  }
}

/**
 * Reads one reference's display, joining a read of the same reference that is already in flight.
 * The read leaves the in-flight map once it settles, so a failed read is retried by the next run.
 *
 * A reference that carries its own `display` resolves without a request, so it never joins or
 * starts a shared read: another caller's name cannot decide this caller's result.
 *
 * @param medplum The Medplum client.
 * @param referenceString The reference's `ResourceType/id` string, the in-flight key.
 * @param reference The reference to read.
 * @returns The resolution: a display name, or `isResolved: false` for a transient failure.
 */
function readReferenceDisplay(
  medplum: MedplumClient,
  referenceString: string,
  reference: Reference,
): Promise<ReferenceResolution> {
  if (reference.display) return readOnce(medplum, referenceString, reference);

  const clientReads = inFlightReads.get(medplum) ?? new Map<string, Promise<ReferenceResolution>>();
  inFlightReads.set(medplum, clientReads);

  const existingRead = clientReads.get(referenceString);
  if (existingRead) return existingRead;

  const read = readOnce(medplum, referenceString, reference).finally(() => {
    clientReads.delete(referenceString);
  });
  clientReads.set(referenceString, read);
  return read;
}

/**
 * Resolves the display name for each reference, filling `cache` in place.
 *
 * Only references missing from the cache are read, and each distinct reference is read once. A name
 * already on `reference.display` is used without a read. A reference another run is already reading
 * joins that read, so overlapping runs never duplicate a read or its Sentry report.
 *
 * A failed read (timeout, dropped connection) is left out of the cache and evicted from the Medplum
 * client's request cache, so the next run retries it with a real request.
 * A resource the viewer's AccessPolicy cannot read, or one that was deleted (403/404/410), is cached as
 * `""`. That outcome cannot change mid-session, and retrying it would cost a request and a Sentry
 * report on every run.
 *
 * @param medplum The Medplum client.
 * @param references The references to resolve. Entries without a `reference` string are ignored.
 * @param cache Names resolved so far; returned filled.
 * @returns `cache`, now holding every name that resolved or proved unreadable.
 */
export async function resolveReferenceDisplays(
  medplum: MedplumClient,
  references: Reference[],
  cache: ReferenceDisplayCache,
): Promise<ReferenceDisplayCache> {
  const pending = references.reduce<Map<string, Reference>>((distinct, reference) => {
    const referenceString = reference.reference;
    if (!referenceString || cache.has(referenceString) || distinct.has(referenceString)) {
      return distinct;
    }

    distinct.set(referenceString, reference);
    return distinct;
  }, new Map());

  const resolutions = await Promise.all(
    [...pending.entries()].map(([referenceString, reference]) =>
      readReferenceDisplay(medplum, referenceString, reference),
    ),
  );

  // A failed read stays out of the cache, so `cache.has` still reports it as unresolved.
  for (const resolution of resolutions) {
    if (resolution.isResolved) cache.set(resolution.referenceString, resolution.display);
  }

  return cache;
}
